import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../context/BookingContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ArrowLeft, Clock, MapPin, Star, FileText, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const BookingHistory = () => {
  const navigate = useNavigate();
  const { bookingHistory } = useBooking();

  const handleViewInvoice = (booking) => {
    navigate(`/user/invoice/${booking.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/user/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Booking History</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {bookingHistory.length === 0 ? (
          <Card className="p-12 text-center">
            <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">No bookings yet</h3>
            <p className="text-muted-foreground mb-6">Start booking drivers to see your history here</p>
            <Button onClick={() => navigate('/user/dashboard')}>
              Book Now
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookingHistory.map((booking) => (
              <Card key={booking.id} className="p-6 hover:shadow-lg transition-all">
                <div className="flex items-start gap-4">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src="https://i.pravatar.cc/150?img=12" />
                    <AvatarFallback>{booking.driverName?.[0] || 'D'}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{booking.driverName || 'Driver'}</h3>
                        <p className="text-sm text-muted-foreground">Professional Driver</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">₹{booking.amount}</p>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            booking.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {booking.status}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">{booking.pickupLocation}</span>
                      </div>
                      {booking.dropLocation && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-green-400" />
                          <span className="text-muted-foreground">{booking.dropLocation}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-muted-foreground/70" />
                        <span className="text-muted-foreground">
                          {format(new Date(booking.createdAt), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground/70" />
                        <span className="text-muted-foreground">{booking.hours} Hour(s)</span>
                      </div>
                      {booking.carTypeName && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">🚗 {booking.carTypeName}</span>
                        </div>
                      )}
                    </div>

                    {booking.rating && (
                      <div className="flex items-center gap-2 mb-4">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={`star-${booking.id}-${star}`}
                            className={`w-4 h-4 ${
                              star <= booking.rating
                                ? 'text-yellow-500 fill-current'
                                : 'text-muted-foreground/50'
                            }`}
                          />
                        ))}
                      </div>
                        {booking.review && (
                          <span className="text-sm text-muted-foreground">"{booking.review}"</span>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(booking)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Invoice
                      </Button>
                      {booking.status === 'completed' && !booking.rating && (
                        <Button variant="outline" size="sm">
                          <Star className="w-4 h-4 mr-2" />
                          Rate Driver
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingHistory;
